import api from '@/services/apiService';

export interface WalletDataResponse {
  balance: number;
  total_income: number;
  saldo_available: number;
  saldo_failed: number;
  saldo_pending: number;
  payment_provider?: string;
  withdraw_fee?: number;
  error?: string;
  message?: string;
}

export interface WalletSyncResponse {
  message?: string;
  error?: string;
}

export interface WithdrawalResponse {
  wallet_id: string;
  total_pending_amount: number;
  total_processing_amount: number;
  withdrawals: Array<{
    withdrawal_code: string;
    amount: number;
    total_amount: number;
    bank_code: string;
    bank_name: string;
    account_number: string;
    account_holder_name: string;
    status: string;
    requested_at: string; // ISO datetime
  }> | null;
  error?: string;
}

export interface WalletBankAccountResponse {
  id: string;
  wallet_id: string;
  bank_code: string;
  bank_name: string;
  account_number: string;
  account_holder_name: string;
  is_primary: boolean;
  is_verified: boolean;
  verified_at: string | null;
  status: string;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  error?: string;
}

export interface WithdrawalRequestResponse {
  code: string;
  error?: string;
}

// /wallet
export const getWalletData = async (): Promise<WalletDataResponse> => {
  try {
    const response = await api.get('/wallet');

    // Defensive: if axios didn't throw for non-2xx, handle it here
    if (response.status !== 200) {
      const msg =
        response.data?.error ??
        response.data?.message ??
        `Request failed with status ${response.status}`;
      return { error: msg } as WalletDataResponse;
    }

    const data = response.data as WalletDataResponse;

    // Check if server returned error field in 200 response (server-side error)
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: data.error } as WalletDataResponse;
    }

    // Check if server returned message field as error indicator in 200 response
    if (data && typeof data === 'object' && 'message' in data && data.message && !data.error) {
      // If there's a message but no wallet id, treat message as error
      return { error: data.message } as WalletDataResponse;
    }

    // On successful 200 with valid wallet data, ensure we don't return error field
    if (data && typeof data === 'object' && (data as any).error) {
      delete (data as any).error;
    }

    return data;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ??
      err?.response?.data?.message ??
      (err?.response?.status
        ? `Request failed with status ${err.response.status}`
        : err?.message) ??
      'Unknown error';
    return { error: msg } as WalletDataResponse;
  }
};

// /wallet/sync
export const syncWallet = async (): Promise<WalletSyncResponse> => {
  try {
    const response = await api.get('/wallet/sync');

    // Defensive: if axios didn't throw for non-2xx, handle it here
    if (response.status !== 200) {
      const msg =
        response.data?.error ??
        response.data?.message ??
        `Request failed with status ${response.status}`;
      return { error: msg } as WalletSyncResponse;
    }

    const data = response.data as WalletSyncResponse;

    // Check if server returned error field in 200 response (server-side error)
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: data.error } as WalletSyncResponse;
    }

    // On successful 200 with sync response, ensure we don't return error field
    if (data && typeof data === 'object' && (data as any).error) {
      delete (data as any).error;
    }

    // If no message provided, set default success message
    if (!data.message) {
      data.message = 'Wallet synced successfully';
    }

    return data;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ??
      err?.response?.data?.message ??
      (err?.response?.status
        ? `Request failed with status ${err.response.status}`
        : err?.message) ??
      'Unknown error';
    return { error: msg } as WalletSyncResponse;
  }
};

// /wallet/withdrawals
export const getWithdrawals = async (
  sortBy?: string,
  order?: string
): Promise<WithdrawalResponse> => {
  try {
    const response = await api.get('/wallet/withdrawals', {
      params: {
        sort_by: sortBy,
        order: order,
      },
    });

    // Defensive: if axios didn't throw for non-2xx, handle it here
    if (response.status !== 200) {
      const msg =
        response.data?.error ??
        response.data?.message ??
        `Request failed with status ${response.status}`;
      return { error: msg } as WithdrawalResponse;
    }

    const data = response.data as WithdrawalResponse;

    // Check if server returned error field in 200 response (server-side error)
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: data.error } as WithdrawalResponse;
    }

    // On successful 200 with valid withdrawal data, ensure we don't return error field
    if (data && typeof data === 'object' && (data as any).error) {
      delete (data as any).error;
    }

    return data;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ??
      err?.response?.data?.message ??
      (err?.response?.status
        ? `Request failed with status ${err.response.status}`
        : err?.message) ??
      'Unknown error';
    return { error: msg } as WithdrawalResponse;
  }
};

export const getBankAccount = async (): Promise<WalletBankAccountResponse> => {
  try {
    const response = await api.get('/wallet/bank-account');

    // Defensive: if axios didn't throw for non-2xx, handle it here
    if (response.status !== 200) {
      const msg =
        response.data?.error ??
        response.data?.message ??
        `Request failed with status ${response.status}`;
      return { error: msg } as WalletBankAccountResponse;
    }

    const data = response.data as WalletBankAccountResponse;

    // Check if server returned error field in 200 response (server-side error)
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: data.error } as WalletBankAccountResponse;
    }

    // On successful 200 with valid bank account data, ensure we don't return error field
    if (data && typeof data === 'object' && (data as any).error) {
      delete (data as any).error;
    }

    return data;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ??
      err?.response?.data?.message ??
      (err?.response?.status
        ? `Request failed with status ${err.response.status}`
        : err?.message) ??
      'Unknown error';
    return { error: msg } as WalletBankAccountResponse;
  }
};

export const requestWithdrawal = async (amount: number): Promise<WithdrawalRequestResponse> => {
  try {
    const response = await api.post('/wallet/withdrawal', { amount });

    // Defensive: if axios didn't throw for non-2xx, handle it here
    if (response.status !== 200 && response.status !== 201) {
      const msg =
        response.data?.error ??
        response.data?.message ??
        `Request failed with status ${response.status}`;
      return { error: msg } as WithdrawalRequestResponse;
    }

    const data = response.data as WithdrawalRequestResponse;

    // Check if server returned error field in 200/201 response (server-side error)
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: data.error } as WithdrawalRequestResponse;
    }

    // On successful 200/201 with valid withdrawal request data, ensure we don't return error field
    if (data && typeof data === 'object' && (data as any).error) {
      delete (data as any).error;
    }

    return data;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ??
      err?.response?.data?.message ??
      (err?.response?.status
        ? `Request failed with status ${err.response.status}`
        : err?.message) ??
      'Unknown error';
    return { error: msg } as WithdrawalRequestResponse;
  }
};

export const createWallet = async (): Promise<void> => {
  await api.post('/wallet/create', {});
};

export interface KYCRequest {
  id: string;
  arena_id?: number | null;
  community_id?: number | null;
  status: 'pending' | 'approved' | 'rejected';
  full_name: string;
  phone_number: string;
  email: string;
  id_card_number: string;
  id_card_photo: string;
  selfie_photo: string;
  bank_name: string;
  account_number: string;
  account_holder_name: string;
  address: string;
  npwp?: string | null;
  rejection_reason?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: number | null;
  created_at: string;
  updated_at: string;
  arena?: { name: string; slug: string } | null;
  community?: { name: string; slug?: string | null } | null;
}

export interface KYCWalletInfo {
  id?: string;
  xendit_user_id?: string | null;
}

export interface KYCStatusResponse {
  has_wallet: boolean;
  has_active_payment_account?: boolean;
  wallet?: KYCWalletInfo | null;
  kyc_status: 'not_applied' | 'pending' | 'approved' | 'rejected';
  kyc_request?: KYCRequest | null;
  prefill: {
    full_name: string;
    phone_number: string;
    email: string;
  };
  error?: string;
}

export function hasActivePaymentAccount(
  kyc?: Pick<KYCStatusResponse, 'has_active_payment_account' | 'wallet'> | null
): boolean {
  if (!kyc) return false;
  if (kyc.has_active_payment_account) return true;
  return Boolean(kyc.wallet?.xendit_user_id);
}

export interface SubmitKYCPayload {
  full_name: string;
  phone_number: string;
  email: string;
  id_card_number: string;
  bank_name: string;
  account_number: string;
  account_holder_name: string;
  address: string;
  npwp?: string;
  id_card_photo: File;
  selfie_photo: File;
}

export const getKYCStatus = async (): Promise<KYCStatusResponse> => {
  try {
    const response = await api.get('/wallet/kyc');
    return response.data as KYCStatusResponse;
  } catch (e) {
    const err = e as any;
    const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
    return { error: msg } as KYCStatusResponse;
  }
};

export const submitKYC = async (
  payload: SubmitKYCPayload
): Promise<{ message?: string; error?: string }> => {
  try {
    const formData = new FormData();
    formData.append('full_name', payload.full_name);
    formData.append('phone_number', payload.phone_number);
    formData.append('email', payload.email);
    formData.append('id_card_number', payload.id_card_number);
    formData.append('bank_name', payload.bank_name);
    formData.append('account_number', payload.account_number);
    formData.append('account_holder_name', payload.account_holder_name);
    formData.append('address', payload.address);
    if (payload.npwp) {
      formData.append('npwp', payload.npwp);
    }
    formData.append('id_card_photo', payload.id_card_photo);
    formData.append('selfie_photo', payload.selfie_photo);

    const response = await api.post('/wallet/kyc', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (e) {
    const err = e as any;
    const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
    return { error: msg };
  }
};

export const listKYCRequests = async (
  status?: string,
  page: number = 1,
  limit: number = 10
): Promise<{ data: KYCRequest[]; pagination: any; error?: string }> => {
  try {
    const response = await api.get('/admin/kyc', {
      params: { status, page, limit },
    });
    return response.data;
  } catch (e) {
    const err = e as any;
    const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
    return { error: msg } as any;
  }
};

export const getKYCRequestDetails = async (
  id: string
): Promise<KYCRequest & { error?: string }> => {
  try {
    const response = await api.get(`/admin/kyc/${id}`);
    return response.data;
  } catch (e) {
    const err = e as any;
    const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
    return { error: msg } as any;
  }
};

export const reviewKYCRequest = async (
  id: string,
  status: 'approved' | 'rejected',
  rejectionReason?: string
): Promise<{ message?: string; error?: string }> => {
  try {
    const response = await api.post(`/admin/kyc/${id}/review`, {
      status,
      rejection_reason: rejectionReason,
    });
    return response.data;
  } catch (e) {
    const err = e as any;
    const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
    return { error: msg };
  }
};
